import { AuthorizationDocument } from '../../Authorization.graphql';
import {
  GqlAllocationKind,
  GqlAuthKind,
  GqlAuthorization,
  GqlAuthorizationGroup,
} from '../../../../graphql/base-types.graphql';
import { Trans } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Switch } from '@startuptools/ui/Switch';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import {
  useCreateAuthorizationMutation,
  useDeleteAuthorizationMutation,
  useUpdateAuthorizationMutation,
} from '../../../../graphql/react-operations';
import { Card } from '@startuptools/ui/Card';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { ReactNode } from 'react';

const AlertPreference: Record<GqlAuthKind, ReactNode> = {
  [GqlAuthKind.Issue]: (
    <Trans id='A share issue may be done with preference for existing shareholders, or without preference. The latter is often called a "directed issue" and is the norm when bringing in new investors. An authorization that gives the board the right to issue shares without preference must be approved by at least 2/3 of the shares represented at the general meeting.' />
  ),
  [GqlAuthKind.Warrants]: (
    <Trans id='Warrants may be issued with preference for existing shareholders, or without. The latter is often called a "directed issue" and is the norm when taking in new investors. Warrants are almost always issued to a specific group of persons, so the most common way of doing it is without preference.' />
  ),
  [GqlAuthKind.Convertibles]: <></>,
  [GqlAuthKind.Bonds]: <></>,
};

const headerPayment: Record<GqlAuthKind, ReactNode> = {
  [GqlAuthKind.Issue]: <Trans id="How shall the new shares be paid for?" />,
  [GqlAuthKind.Warrants]: <Trans id="How shall the warrants be paid for?" />,
  [GqlAuthKind.Convertibles]: <Trans id="How shall the convertibles be paid for?" />,
  [GqlAuthKind.Bonds]: <></>,
};

const alertPayment: Record<GqlAuthKind, ReactNode> = {
  [GqlAuthKind.Issue]: (
    <Trans
      id={`Payment for newly issued shares are normally done in cash, that is by depositing money at the company's bank account. But it is also possible to pay by offsetting a debt or by payment "in kind", which means that something of value is contributed to the company. Select the payment methods that should be allowed.`}
    />
  ),
  [GqlAuthKind.Warrants]: (
    <Trans
      id={`Payment for warrants is normally made in cash, that is by depositing money to the company's bank account. But it is also possible to pay by offsetting a debt or by payment "in kind", that is, by contributing something of value to the company. Select the methods of payments that shall be allowed.`}
    />
  ),
  [GqlAuthKind.Convertibles]: (
    <Trans
      id={`Payment for convertibles is normally made in cash, that is by depositing money to the company's bank account. But it is also possible to pay by offsetting a debt or by payment "in kind", that is, by contributing something of value to the company. Select the methods of payments that shall be allowed.`}
    />
  ),
  [GqlAuthKind.Bonds]: <></>,
};

const headerMaxRaiseMap: Record<GqlAuthKind, ReactNode> = {
  [GqlAuthKind.Issue]: <Trans id="What is the largest number of shares that can be issued?" />,
  [GqlAuthKind.Warrants]: <Trans id="What is the largest number of warrants that can be issued?" />,
  [GqlAuthKind.Convertibles]: <Trans id="What is the largest number of convertibles that can be issued?" />,
  [GqlAuthKind.Bonds]: <></>,
};

const alertMaxRaiseMap: Record<GqlAuthKind, ReactNode> = {
  [GqlAuthKind.Issue]: (
    <Trans id="The authorization may either be limited to a certain number of shares, or as many shares as is allowed by the articles of association" />
  ),
  [GqlAuthKind.Warrants]: (
    <Trans id="The authorization may be limited to a certain number of warrants (each representing one share), or to as many warrants as are allowed by the articles of association." />
  ),
  [GqlAuthKind.Convertibles]: (
    <Trans id="The authorization may be limited to a certain number of convertibles (each representing one share), or to as many convertibles as are allowed by the articles of association." />
  ),
  [GqlAuthKind.Bonds]: <></>,
};

const radioMaxRaiseMap: Record<GqlAuthKind, ReactNode> = {
  [GqlAuthKind.Issue]: <Trans id="As many shares that fit within the articles of associations share limits" />,
  [GqlAuthKind.Warrants]: <Trans id="As many warrants that fit within the articles of associations share limits" />,
  [GqlAuthKind.Convertibles]: (
    <Trans id="As many convertibles that fit within the articles of associations share limits" />
  ),
  [GqlAuthKind.Bonds]: <></>,
};

type FormValues = Pick<
  GqlAuthorization,
  'allocation' | 'offsetPayment' | 'cashPayment' | 'inKindPayment' | 'purposeTextPl' | 'purposeTextSl'
> & { singleUse: 'true' | 'false'; maxRaiseByAoa: 'true' | 'false'; maxRaise: string | null };

const Header = ({ kind }: { kind: GqlAuthKind }) => {
  switch (kind) {
    case GqlAuthKind.Issue:
      return <Trans id="Share Issue" />;
    case GqlAuthKind.Warrants:
      return <Trans id="Warrants" />;
    case GqlAuthKind.Convertibles:
      return <Trans id="Convertibles" />;
    case GqlAuthKind.Bonds:
      return <Trans id="Bonds" />;
  }
};

const AuthName = ({ kind }: { kind: GqlAuthKind }) => {
  switch (kind) {
    case GqlAuthKind.Issue:
      return <Trans id="share issue" />;
    case GqlAuthKind.Warrants:
      return <Trans id="warrants" />;
    case GqlAuthKind.Convertibles:
      return <Trans id="convertibles" />;
    case GqlAuthKind.Bonds:
      return <Trans id="bond" />;
  }
};

const AuthNamePlural = ({ kind }: { kind: GqlAuthKind }) => {
  switch (kind) {
    case GqlAuthKind.Issue:
      return <Trans id="share issues" />;
    case GqlAuthKind.Warrants:
      return <Trans id="warrants" />;
    case GqlAuthKind.Convertibles:
      return <Trans id="convertibles" />;
    case GqlAuthKind.Bonds:
      return <Trans id="bonds" />;
  }
};

const AgStepAuthorizationForm = ({
  auth,
  authGroup,
  stepNext,
}: {
  auth: Pick<
    GqlAuthorization,
    | 'id'
    | 'kind'
    | 'allocation'
    | 'offsetPayment'
    | 'cashPayment'
    | 'inKindPayment'
    | 'purposeTextPl'
    | 'purposeTextSl'
    | 'maxRaiseByAoa'
    | 'maxRaise'
    | 'singleUse'
  >;
  authGroup: Pick<GqlAuthorizationGroup, 'companyId' | 'primaryLang' | 'secondaryLang'>;
  stepNext: () => void;
}) => {
  const [doUpdate, { loading: updateLoading }] = useUpdateAuthorizationMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });

  const { control, register, watch, handleSubmit } = useForm<FormValues>({
    defaultValues: auth
      ? {
          allocation: auth.allocation,
          cashPayment: auth.cashPayment,
          inKindPayment: auth.inKindPayment,
          offsetPayment: auth.offsetPayment,
          maxRaise: auth.maxRaise,
          maxRaiseByAoa: auth.maxRaiseByAoa ? 'true' : 'false',
          purposeTextPl: auth.purposeTextPl,
          purposeTextSl: auth.purposeTextSl,
          singleUse: auth.singleUse ? 'true' : 'false',
        }
      : undefined,
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ singleUse, maxRaiseByAoa, ...values }) => {
    if (!auth) {
      return;
    }
    try {
      await doUpdate({
        variables: {
          companyId: authGroup.companyId,
          input: {
            id: auth.id,
            kind: auth.kind,
            singleUse: singleUse === 'true',
            maxRaiseByAoa: maxRaiseByAoa === 'true',
            ...values,
          },
        },
      });
      return stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  const currentMaxRaiseByAoa = watch('maxRaiseByAoa');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Card>
          <Typography>
            <Trans id="Shall it be possible to use the authorization once or multiple times?" />
          </Typography>
          <Alert>
            <Trans id="An authorization can either be created to be used once or to be able to be used multiple times" />
          </Alert>
          <Controller
            control={control}
            name="singleUse"
            render={({ field }) => (
              <RadioGroup {...field}>
                <Radio value="true" label={<Trans id="One time" />} />
                <Radio value="false" label={<Trans id="Many times" />} />
              </RadioGroup>
            )}
          />
        </Card>

        <Card>
          <Typography>
            <Trans
              id="Shall the {authName} be made with or without preference?"
              values={{ authName: <AuthNamePlural kind={auth.kind} /> }}
            />
          </Typography>
          {auth.kind !== GqlAuthKind.Convertibles && <Alert>{AlertPreference[auth.kind]}</Alert>}
          <Controller
            control={control}
            name="allocation"
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="The authorization is valid for..." />
                </FormLabel>
                <RadioGroup {...field}>
                  <Radio
                    value={GqlAllocationKind.OnlyWithPreferentialRights}
                    label={
                      <Trans
                        id="Only {authName} <b>with</b> preference for existing shareholders"
                        components={{ b: <b /> }}
                        values={{ authName: <AuthNamePlural kind={auth.kind} /> }}
                      />
                    }
                  />
                  <Radio
                    value={GqlAllocationKind.OnlyWithoutPreferentialRights}
                    label={
                      <Trans
                        id="Only {authName} <b>without</b> preference for existing shareholders"
                        components={{ b: <b /> }}
                        values={{ authName: <AuthNamePlural kind={auth.kind} /> }}
                      />
                    }
                  />
                  <Radio
                    value={GqlAllocationKind.WithOrWithoutPreferentialRights}
                    label={
                      <Trans
                        id="{authName} with or without preference for existing shareholders"
                        values={{
                          authName: (
                            <Typography sx={{ textTransform: 'capitalize' }}>
                              <AuthNamePlural kind={auth.kind} />
                            </Typography>
                          ),
                        }}
                      />
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Card>

        <Card>
          <Typography>{headerPayment[auth.kind]}</Typography>
          <Alert>{alertPayment[auth.kind]}</Alert>

          <Controller
            control={control}
            name="cashPayment"
            render={({ field: { onChange, value, ...field } }) => (
              <Checkbox
                {...field}
                checked={value}
                onChange={ev => onChange(ev.target.checked)}
                label={<Trans id="Cash" />}
              />
            )}
          />
          <Controller
            control={control}
            name="offsetPayment"
            render={({ field: { onChange, value, ...field } }) => (
              <Checkbox
                {...field}
                checked={value}
                onChange={ev => onChange(ev.target.checked)}
                label={<Trans id="Through Offset" />}
              />
            )}
          />
          <Controller
            control={control}
            name="inKindPayment"
            render={({ field: { onChange, value, ...field } }) => (
              <Checkbox
                {...field}
                checked={value}
                onChange={ev => onChange(ev.target.checked)}
                label={<Trans id="Through In-Kind" />}
              />
            )}
          />
        </Card>

        <Card>
          {authGroup.primaryLang && (
            <FormControl required>
              <FormLabel>
                <Trans id="Purpose (Swedish)" />
              </FormLabel>
              <Input {...register('purposeTextPl')} required />
            </FormControl>
          )}
          {authGroup.secondaryLang && (
            <FormControl required>
              <FormLabel>
                <Trans id="Purpose (English)" />
              </FormLabel>
              <Input {...register('purposeTextSl')} required />
            </FormControl>
          )}
        </Card>

        <Card>
          <Typography>{headerMaxRaiseMap[auth.kind]}</Typography>
          <Alert>{alertMaxRaiseMap[auth.kind]}</Alert>

          <Controller
            control={control}
            name="maxRaiseByAoa"
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="The authorization gives the board mandate to emit..." />
                </FormLabel>
                <RadioGroup {...field}>
                  <Radio value="true" label={radioMaxRaiseMap[auth.kind]} />
                  <Radio value="false" label={<Trans id="Other..." />} />
                </RadioGroup>
              </FormControl>
            )}
          />
          {currentMaxRaiseByAoa === 'false' && (
            <FormControl required>
              <FormLabel>
                <Trans id="Max Number of Shares" />
              </FormLabel>
              <Controller
                control={control}
                name="maxRaise"
                render={({ field: { value, onChange, ...field } }) => (
                  <NumericFormatInput {...field} value={value ?? ''} onChange={onChange} decimalScale={0} required />
                )}
              />
            </FormControl>
          )}
        </Card>

        <Box>
          <Button type="submit" loading={updateLoading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

export const AgStepAuthorization = ({
  authorizationGroup,
  kind,
}: {
  authorizationGroup: Pick<GqlAuthorizationGroup, 'id' | 'companyId' | 'secondaryLang' | 'primaryLang'> & {
    authorizations: Pick<
      GqlAuthorization,
      | 'id'
      | 'kind'
      | 'allocation'
      | 'offsetPayment'
      | 'cashPayment'
      | 'inKindPayment'
      | 'purposeTextPl'
      | 'purposeTextSl'
      | 'maxRaiseByAoa'
      | 'maxRaise'
      | 'singleUse'
    >[];
  };
  kind: GqlAuthKind;
}) => {
  const auth = authorizationGroup.authorizations.find(a => a.kind === kind);

  const { stepNext } = useStepper();

  const [doDelete, { loading: deleteLoading }] = useDeleteAuthorizationMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });

  const [doCreate, { loading: createLoading }] = useCreateAuthorizationMutation({
    refetchQueries: [AuthorizationDocument],
    awaitRefetchQueries: true,
  });

  const handleSwitchOnChange = async (checked: boolean) => {
    if (!checked && auth) {
      await doDelete({ variables: { companyId: authorizationGroup.companyId, id: auth.id } });
    }
    if (checked && !auth) {
      await doCreate({
        variables: {
          companyId: authorizationGroup.companyId,
          input: {
            authorizationGroupId: authorizationGroup.id,
            kind,
          },
        },
      });
    }
  };

  const loading = deleteLoading || createLoading;

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Header kind={kind} />
      </Typography>

      <Box>
        <Switch
          disabled={loading}
          checked={Boolean(auth)}
          onChange={c => handleSwitchOnChange(c.target.checked)}
          endDecorator={
            <Trans id="Create an authorization for {authName}" values={{ authName: <AuthName kind={kind} /> }} />
          }
        />
      </Box>

      {auth ? (
        <AgStepAuthorizationForm authGroup={authorizationGroup} auth={auth} stepNext={stepNext} />
      ) : (
        <Box>
          <Button onClick={() => stepNext()}>
            <Trans id="Skip" />
          </Button>
        </Box>
      )}
    </Stack>
  );
};
